/**
 * @fileoverview Complete inspectable-entity and inspectable-entity-group
 *               schemas for the complex-inspections contract bundle
 *               (document 11).
 * @module import-core/contracts/complex-inspections/entitySchemas
 *
 * Responsibilities:
 * - Define the complete polymorphic `inspectableEntities` document schema with
 *   all seven categories, every category-detail contract, source/provenance,
 *   lifecycle, vendor, and hydrant arrays, plus the mutually exclusive
 *   `complexAnchor` / `complexMembership` facets.
 * - Define the complete `inspectableEntityGroups` document schema with
 *   anchor snapshot, contacts, and anchor history, plus the cleanup-only
 *   variant `inspectableEntityGroupCleanupDocumentSchema` used solely by
 *   detach/delete seams on pre-existing malformed groups.
 * - Export `inspectableEntityWritableSchema` — the client-writable field
 *   projection consumed by host create/update validators.
 * - Export `isCanonicalEntityDocument` plus the canonical/legacy Mongo match
 *   fragments — the single authority for "which entity documents are
 *   canonical" across routes, services, and sync normalization.
 * - Normalize null address fields and optional legacy contact-link fields at
 *   target-document synchronization and write boundaries.
 *
 * Dependencies:
 * - `zod` v3; `./sharedSchemas` primitives; `./contactSchemas` link schemas;
 *   `./embeddedBuildingSchemas` (`systems` / `intel` / `riskProfile` /
 *   `riskAssessment` embedded blocks).
 *
 * Architecture:
 * - Persisted-boundary schemas. Accept BSON `ObjectId`/`Date` and serialized
 *   string/ISO forms. `.strict()` at the containing-document boundary plus
 *   `superRefine` for cross-field invariants (facet mutual exclusion and
 *   category-detail discrimination, including `never` fields on new writes).
 *
 * Notes:
 * - `entityCategory` is the physical discriminator; a building or parcel that
 *   anchors a complex keeps its category and details and adds `complexAnchor`.
 *   `entityCategory='complex'` is reserved for genuinely abstract
 *   common-area/campus records; complex facets (`complexAnchor` /
 *   `complexMembership`) are valid only on `building`/`parcel` entities, and
 *   membership carries a required `anchorEntityCategory` snapshot so
 *   homogeneous membership is provable offline.
 */

import { z } from 'zod';
import {
  objectIdLikeSchema,
  isoDateLikeSchema,
  lookupReferenceSchema,
  contactEmailSchema,
  contactPhoneSchema,
  legacySourceSchema,
  postalAddressSchema,
  entityCategorySchema,
  complexEligibleEntityCategorySchema,
  isComplexEligibleEntityCategory,
  auditFieldsSchema,
  tombstoneFieldsSchema,
} from './sharedSchemas';
import {
  embeddedAttachmentRefSchema,
  embeddedBuildingSystemSchema,
  embeddedChemicalSnapshotSchema,
  embeddedCommunicationLogSnapshotSchema,
  embeddedExternalLinksSchema,
  embeddedLegacyHistorySnapshotSchema,
  embeddedPermitSnapshotSchema,
  embeddedRiskAssessmentSchema,
  embeddedRiskProfileSchema,
  embeddedTankSnapshotSchema,
  embeddedTceSnapshotSchema,
  intelSnapshotSchema,
} from './embeddedBuildingSchemas';
import {
  complexGroupContactLinkSchema,
  entityContactLinkSchema,
  normalizeEntityContactLink,
} from './contactSchemas';

// ---------------------------------------------------------------------------
// Location / service areas / relationships (document 11 §2)
// ---------------------------------------------------------------------------

const geoJsonPointSchema = z.object({
  type: z.literal('Point'),
  coordinates: z.tuple([z.number(), z.number()]),
});

const geoJsonPolygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(z.array(z.tuple([z.number(), z.number()]))),
});

const geoJsonMultiPolygonSchema = z.object({
  type: z.literal('MultiPolygon'),
  coordinates: z.array(z.array(z.array(z.tuple([z.number(), z.number()])))),
});

const entityLocationAddressSchema = z.object({
  fullAddress: z.string(),
  streetNumber: z.string().optional(),
  predirectional: z.string().optional(),
  streetName: z.string().optional(),
  streetSuffix: z.string().optional(),
  postdirectional: z.string().optional(),
  unitDesignator: z.string().optional(),
  unitNumber: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  zip4: z.string().optional(),
  county: z.string().optional(),
  country: z.string().optional(),
});

const entityLocationAddressMetadataSchema = z.object({
  additionalAddressLine: z.string().optional(),
  postalCode5: z.string().optional(),
  urbanization: z.string().optional(),
  originalAddress: z.string().optional(),
  locationName: z.string().optional(),
  addressSource: z.string().optional(),
});

const entityLocationSchema = z.object({
  locationType: z.enum(['parcel', 'address', 'coordinates', 'description']),
  locationDescription: z.string().optional(),
  nearestCrossStreet: z.string().optional(),
  coordinates: geoJsonPointSchema.optional(),
  address: entityLocationAddressSchema.optional(),
  addressMetadata: entityLocationAddressMetadataSchema.optional(),
  boundary: z.union([geoJsonPolygonSchema, geoJsonMultiPolygonSchema]).optional(),
  hasBoundary: z.boolean().optional(),
});

const serviceAreaSchema = z
  .object({
    typeCode: z.string(),
    typeName: z.string(),
    value: lookupReferenceSchema,
  })
  .superRefine((val, ctx) => {
    if (val.value.lookupType !== val.typeCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value', 'lookupType'],
        message: 'serviceAreas.value.lookupType must match typeCode',
      });
    }
  });

const relatedEntitySchema = z
  .object({
    entityId: objectIdLikeSchema,
    entityCategory: entityCategorySchema,
    entityName: z.string(),
    relationship: z.string(),
    isPrimary: z.boolean().optional(),
    distance: z.union([z.string(), z.number()]).optional(),
    comments: z.string().optional(),
  })
  .passthrough();

// ---------------------------------------------------------------------------
// Ownership / parcel provenance / source metadata (document 11 §6)
// ---------------------------------------------------------------------------

const entityOwnerSchema = z.object({
  name: z.string().min(1),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  ownerType: z.string().optional(),
  sourceSlot: z.number().int().positive().optional(),
  isPrimary: z.boolean(),
});

const entityOwnershipSchema = z
  .object({
    owners: z.array(entityOwnerSchema),
    mailingAddress: postalAddressSchema.optional(),
    source: z.enum(['imported', 'manual', 'inherited', 'linked', 'parcelOwnerUpdate', 'field']),
    sourceAsOf: isoDateLikeSchema.optional(),
    scope: z.enum(['land', 'building', 'occupancy', 'complex', 'site', 'hydrant']),
    status: z.enum(['confirmed', 'inherited', 'conflict', 'unknown']).optional(),
    inheritedFromEntityId: objectIdLikeSchema.optional(),
    careOf: z.string().optional(),
    previousOwnerName: z.string().optional(),
    originalOwnerName: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    const primaryCount = value.owners.filter((owner) => owner.isPrimary).length;
    if (primaryCount > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['owners'],
        message: 'ownership may contain at most one primary owner',
      });
    }
  });

const parcelIdentifiersSchema = z.object({
  normalizedParcelNumber: z.string().optional(),
  stateParcelNumber: z.string().optional(),
  accountNumber: z.string().optional(),
  taxId: z.string().optional(),
  alternateParcelNumbers: z.array(z.string()).optional(),
});

const sourceMetadataSchema = z
  .object({
    sourceSystem: z.string().optional(),
    sourceRecordId: z.string().optional(),
    sourceLocator: z.string().optional(),
    sourceUrl: z.string().optional(),
    sourceUpdatedAt: isoDateLikeSchema.optional(),
    lastRefreshAt: isoDateLikeSchema.optional(),
    sourceRevisionDate: isoDateLikeSchema.optional(),
    importRunId: z.string().optional(),
    importedAt: isoDateLikeSchema.optional(),
    rawHash: z.string().optional(),
    sourceObjectId: z.string().optional(),
    stackId: z.string().optional(),
    sourceSpecific: z.record(z.unknown()).optional(),
  })
  .passthrough();

const customFieldValueSchema = z
  .object({
    // Field ids are custom-field definition keys. Shipped data and the app type
    // use definition slugs; migrated rows may carry 24-hex ObjectIds — both are
    // valid strings here.
    fieldId: z.string(),
    fieldType: z.string(),
    label: z.string(),
    value: z.unknown(),
    displayValue: z.string().optional(),
    legacyCode: z.string().optional(),
    updatedAt: isoDateLikeSchema,
    updatedBy: objectIdLikeSchema,
  })
  .passthrough();

const entityLifecycleEventSchema = z.object({
  at: isoDateLikeSchema,
  by: z.union([objectIdLikeSchema, z.literal('system')]),
  action: z.enum([
    'regrid_flagged',
    'regrid_resolved',
    'status_changed',
    'hard_deleted',
    'successor_linked',
    'predecessor_linked',
  ]),
  fromStatus: z.string().optional(),
  toStatus: z.string().optional(),
  note: z.string().optional(),
  metadata: z.record(z.unknown()).optional(),
});

const entityLifecycleSchema = z.object({
  history: z.array(entityLifecycleEventSchema).optional(),
});

const vendorDataSchema = z.object({
  vendor: z.enum(['reportAll', 'regrid', 'manual']),
  vendorRecordId: z.string().optional(),
  lastVendorSync: isoDateLikeSchema,
  vendorUpdateDate: isoDateLikeSchema.optional(),
  rawHash: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Category-detail contracts (document 11 §7)
// ---------------------------------------------------------------------------

const parcelStructureSummarySchema = z.object({
  hasStructure: z.boolean().optional(),
  structureCount: z.number().int().optional(),
  storyCount: z.number().optional(),
  unitCount: z.number().int().optional(),
  roomCount: z.number().optional(),
  bathCount: z.number().optional(),
  partialBathCount: z.number().optional(),
  bedroomCount: z.number().int().optional(),
  structureStyle: z.string().optional(),
  yearBuilt: z.number().int().optional(),
  effectiveYearBuilt: z.number().int().optional(),
  totalBuildingSquareFeet: z.number().optional(),
  recordedStructureSquareFeet: z.number().optional(),
  buildingAreaDefinition: z.string().optional(),
});

const parcelTaxAssessmentSchema = z.object({
  valueType: z.string().optional(),
  totalValue: z.number().optional(),
  landValue: z.number().optional(),
  improvementValue: z.number().optional(),
  agriculturalValue: z.number().optional(),
  taxAmount: z.number().optional(),
  taxYear: z.string().optional(),
});

const parcelLegalReferencesSchema = z.object({
  platNumber: z.string().optional(),
  book: z.string().optional(),
  page: z.string().optional(),
  block: z.string().optional(),
  lotNumber: z.string().optional(),
  legalDescription: z.string().optional(),
  subdivisionName: z.string().optional(),
  neighborhoodName: z.string().optional(),
  neighborhoodCode: z.string().optional(),
});

const parcelGeographicReferencesSchema = z.object({
  censusTract: z.string().optional(),
  censusBlock: z.string().optional(),
  censusBlockGroup: z.string().optional(),
  censusZcta: z.string().optional(),
  censusCountySubdivision: z.string().optional(),
  plssTownship: z.string().optional(),
  plssSection: z.string().optional(),
  plssRange: z.string().optional(),
  qualifiedOpportunityZone: z.boolean().optional(),
  qualifiedOpportunityZoneTract: z.string().optional(),
});

const parcelDetailsSchema = z.object({
  book: z.string().optional(),
  page: z.string().optional(),
  block: z.string().optional(),
  parcel: z.string().optional(),
  lotNumber: z.string().optional(),
  structureSummary: parcelStructureSummarySchema.optional(),
  taxAssessment: parcelTaxAssessmentSchema.optional(),
  legalReferences: parcelLegalReferencesSchema.optional(),
  geographicReferences: parcelGeographicReferencesSchema.optional(),
  acres: z.number().optional(),
  parcelSquareFeet: z.number().optional(),
  lotSizeSqFt: z.number().optional(),
  squareFeet: z.number().optional(),
  yearBuilt: z.number().int().optional(),
  bathCount: z.number().optional(),
  bedroomCount: z.number().int().optional(),
  roomCount: z.number().optional(),
  assessedValue: z.number().optional(),
  assessedLandValue: z.number().optional(),
  assessedImprovementsValue: z.number().optional(),
  marketValue: z.number().optional(),
  taxYear: z.string().optional(),
  taxAmount: z.number().optional(),
  ownerName: z.string().optional(),
  ownerName2: z.string().optional(),
  lastSaleDate: z.string().optional(),
  lastSalePrice: z.number().optional(),
  lastOwnershipTransferDate: z.string().optional(),
  priorSaleDate: z.string().optional(),
  priorSalePrice: z.number().optional(),
  legalDescription: z.string().optional(),
  subdivisionName: z.string().optional(),
  lotSize: z.string().optional(),
  frontage: z.string().optional(),
  topography: z.string().optional(),
  floodZone: z.string().optional(),
  deedReference: z.string().optional(),
  fireHazardLevel: z.enum(['low', 'medium', 'high', 'extreme']).optional(),
  fireHazardSource: z.string().optional(),
  fireHazardInWUI: z.boolean().optional(),
  fireHazardZoneName: z.string().optional(),
  fireHazardStampedAt: isoDateLikeSchema.optional(),
});

const buildingDetailsSchema = z
  .object({
    buildingNumber: z.string().min(1),
    buildingType: lookupReferenceSchema.optional(),
    yearBuilt: z.number().int().optional(),
    squareFeet: z.number().optional(),
    floors: z.number().int().optional(),
    constructionType: lookupReferenceSchema.optional(),
    sprinklered: z.boolean().optional(),
  })
  .passthrough();

const unitDetailsSchema = z.object({
  unitNumber: z.string().min(1),
  floor: z.number().optional(),
  squareFeet: z.number().optional(),
  occupancyType: lookupReferenceSchema.optional(),
  hasSuppression: z.boolean().optional(),
});

const siteDetailsSchema = z.object({
  siteNumber: z.string().min(1),
  siteType: lookupReferenceSchema.optional(),
});

const hydrantDetailsSchema = z
  .object({
    hydrantNumber: z.string().min(1),
    hydrantType: lookupReferenceSchema.optional(),
    manufacturer: z.string().optional(),
    installDate: isoDateLikeSchema.optional(),
    flowRate: z.number().optional(),
    outlets: z.number().int().optional(),
    make: lookupReferenceSchema.optional(),
    model: lookupReferenceSchema.optional(),
    year: z.number().optional(),
    barrelSize: z.number().optional(),
    barrelLength: z.number().optional(),
    waterMainSize: z.number().optional(),
    waterMainType: lookupReferenceSchema.optional(),
    shutOffValveLocation: z.string().optional(),
    shutOffValveSize: z.string().optional(),
    outletSize: z.number().optional(),
    outletType: lookupReferenceSchema.optional(),
    outletCoefficient: z.number().optional(),
    hydrantClass: lookupReferenceSchema.optional(),
    isPrivate: z.boolean().optional(),
    isYardHydrant: z.boolean().optional(),
    isDryHydrant: z.boolean().optional(),
  })
  .passthrough();

const hydrantDefectSchema = z.object({
  defectId: z.string(),
  type: z.union([z.string(), lookupReferenceSchema]).optional(),
  notes: z.string().optional(),
  recordedAt: isoDateLikeSchema.optional(),
  status: z.string().optional(),
});

const hydrantFlowTestSchema = z.object({
  testedAt: isoDateLikeSchema.optional(),
  gpmActual: z.number().optional(),
  staticPsi: z.number().optional(),
  residualPsi: z.number().optional(),
});

const complexDetailsSchema = z.object({
  complexName: z.string().optional(),
  complexType: lookupReferenceSchema.optional(),
});

const crrProfileSchema = z.object({
  homeType: z.union([lookupReferenceSchema, z.string()]).optional(),
  hasEscapePlan: z.boolean().optional(),
  hasPracticedEscapePlan: z.boolean().optional(),
  meetingPlace: z.string().optional(),
  isOwned: z.boolean().optional(),
  totalResidents: z.number().optional(),
  infantsCount: z.number().optional(),
  childrenCount: z.number().optional(),
  adultsCount: z.number().optional(),
  elderlyCount: z.number().optional(),
  disabledCount: z.number().optional(),
  disabledResidents: z.number().optional(),
  smokersCount: z.number().optional(),
  hasWorkingPhone: z.boolean().optional(),
  hasFireExtinguisher: z.boolean().optional(),
  surveyHistory: z
    .array(
      z.object({
        question: z.string(),
        value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
        inspectionDate: isoDateLikeSchema.optional(),
      }),
    )
    .optional(),
});

// ---------------------------------------------------------------------------
// Complex facets (document 11 §4)
// ---------------------------------------------------------------------------

/**
 * @contract complexAnchorLinkSchema
 * Purpose: Reverse link marking this entity as the group's Primary Complex
 *   Entity. `inspectableEntityGroups.anchor` is authoritative; this facet must
 *   agree with it and is updated in the same logical mutation.
 */
export const complexAnchorLinkSchema = z
  .object({
    schemaVersion: z.literal(1),
    groupId: objectIdLikeSchema,
    groupName: z.string(),
    becameAnchorAt: isoDateLikeSchema,
    becameAnchorBy: objectIdLikeSchema,
    updatedAt: isoDateLikeSchema,
    updatedBy: objectIdLikeSchema,
  })
  .strict();

/**
 * @contract complexMembershipSchema
 * Purpose: Non-anchor membership facet. `participationMode` selects governed
 *   vs standalone reporting/billing; snapshots denormalize group/anchor
 *   display values for offline use.
 * Rules: `anchorEntityCategory` is a required eligible-category snapshot of the
 *   group's anchor so offline/full-document validation can prove homogeneous
 *   membership (`entityCategory === anchorEntityCategory`) without loading the
 *   anchor document. Only `building | parcel` may ever be stored here.
 */
export const complexMembershipSchema = z
  .object({
    schemaVersion: z.literal(1),
    groupId: objectIdLikeSchema,
    groupName: z.string(),
    anchorEntityId: objectIdLikeSchema,
    anchorEntityName: z.string(),
    anchorEntityCategory: complexEligibleEntityCategorySchema,
    participationMode: z.enum(['complex-governed', 'standalone']),
    membershipStatus: z.enum(['active', 'inactive']),
    memberRole: lookupReferenceSchema.optional(),
    joinedAt: isoDateLikeSchema,
    joinedBy: objectIdLikeSchema,
    updatedAt: isoDateLikeSchema,
    updatedBy: objectIdLikeSchema,
    legacySource: legacySourceSchema
      .extend({ legacyComplexId: z.string().optional() })
      .optional(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Shared entity field sets
// ---------------------------------------------------------------------------

/**
 * @contract inspectableEntityWritableSchema
 * Purpose: Client-writable canonical entity fields (document 11). Consumed by
 *   host create/update validators as a projection — server-owned `_id`,
 *   `schemaVersion`, timestamps, actors, `version`, and tombstone metadata are
 *   intentionally absent so a `.strict()` input schema rejects them.
 * Rules: every accepted field is explicitly canonical per document 11 §5;
 *   complex facets remain writable here because the Phase 1B operation
 *   boundary — not REST create/update — owns them. Derived/compatibility
 *   fields (`entityAddress`, `isArchived`/`archivedAt`/`archivedBy`) are NOT
 *   client-writable and live only on the persisted document schema below.
 *   Entity-level `legacySource` is not part of the document-11 entity shape
 *   at all — it exists only on groups and membership facets.
 */
export const inspectableEntityWritableSchema = z
  .object({
    entityCategory: entityCategorySchema,
    parentEntityId: objectIdLikeSchema.optional(),
    name: z.string().min(1),
    description: z.string().optional(),
    entityLocation: entityLocationSchema,
    // Required on the persisted document (doc 11 §2) but defaults to [] so
    // legacy REST/sync clients that omit it keep working unchanged.
    serviceAreas: z.array(serviceAreaSchema).default([]),
    relatedEntities: z.array(relatedEntitySchema).optional(),
    contacts: z.array(entityContactLinkSchema).optional(),
    lastInspectionDate: isoDateLikeSchema.optional(),
    nextScheduledInspection: isoDateLikeSchema.optional(),
    inspectionFrequency: lookupReferenceSchema.optional(),
    tags: z.array(z.string()).optional(),
    status: lookupReferenceSchema,
    ownership: entityOwnershipSchema.optional(),
    parcelNumber: z.string().optional(),
    parcelIdentifiers: parcelIdentifiersSchema.optional(),
    propertyType: lookupReferenceSchema.optional(),
    zoning: lookupReferenceSchema.optional(),
    sourceMetadata: sourceMetadataSchema.optional(),
    customFieldValues: z.record(z.string(), customFieldValueSchema).optional(),
    vendorData: vendorDataSchema.optional(),
    lifecycle: entityLifecycleSchema.optional(),
    complexAnchor: complexAnchorLinkSchema.optional(),
    complexMembership: complexMembershipSchema.optional(),
    parcelDetails: parcelDetailsSchema.optional(),
    buildingDetails: buildingDetailsSchema.optional(),
    unitDetails: unitDetailsSchema.optional(),
    siteDetails: siteDetailsSchema.optional(),
    hydrantDetails: hydrantDetailsSchema.optional(),
    hydrantDefects: z.array(hydrantDefectSchema).optional(),
    hydrantFlowTests: z.array(hydrantFlowTestSchema).optional(),
    complexDetails: complexDetailsSchema.optional(),
    crrProfile: crrProfileSchema.optional(),
    mergedIntoApn: z.string().optional(),
    splitIntoApns: z.array(z.string()).optional(),
    // Embedded building-owned blocks (building-systems G-016 + D4): the polymorphic
    // `systems[]` block plus its sibling intel / risk / permit / tank / chemical /
    // history / call-log / TCE / attachment blocks. Preserved verbatim (passthrough)
    // — see embeddedBuildingSchemas.ts.
    systems: z.array(embeddedBuildingSystemSchema).optional(),
    intel: z.array(intelSnapshotSchema).optional(),
    riskProfile: embeddedRiskProfileSchema.optional(),
    riskAssessment: embeddedRiskAssessmentSchema.optional(),
    permits: z.array(embeddedPermitSnapshotSchema).optional(),
    tanks: z.array(embeddedTankSnapshotSchema).optional(),
    chemicals: z.array(embeddedChemicalSnapshotSchema).optional(),
    legacyHistory: z.array(embeddedLegacyHistorySnapshotSchema).optional(),
    communicationLogs: z.array(embeddedCommunicationLogSnapshotSchema).optional(),
    externalLinks: embeddedExternalLinksSchema.optional(),
    tce: embeddedTceSnapshotSchema.optional(),
    attachments: z.array(embeddedAttachmentRefSchema).optional(),
    contactIds: z.array(z.string()).optional(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Category discrimination + facet mutual exclusion (document 11 §8)
// ---------------------------------------------------------------------------

const CATEGORY_DETAIL_KEYS = [
  'parcelDetails',
  'buildingDetails',
  'unitDetails',
  'siteDetails',
  'hydrantDetails',
  'complexDetails',
] as const;

const CATEGORY_RULES: Record<
  z.infer<typeof entityCategorySchema>,
  { required: string[]; allowed: string[]; extra?: (doc: any, ctx: z.RefinementCtx) => void }
> = {
  parcel: { required: [], allowed: ['parcelDetails'] },
  residential: { required: [], allowed: [] },
  building: { required: ['buildingDetails'], allowed: ['buildingDetails'] },
  unit: {
    required: ['unitDetails'],
    allowed: ['unitDetails'],
    extra: (doc, ctx) => {
      if (doc.parentEntityId === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['parentEntityId'],
          message: "parentEntityId is required when entityCategory is 'unit'",
        });
      }
    },
  },
  site: { required: ['siteDetails'], allowed: ['siteDetails'] },
  hydrant: { required: ['hydrantDetails'], allowed: ['hydrantDetails'] },
  // 'complex' is a genuine abstract/common-area category — it does not anchor
  // an inspection complex, so complex facets are prohibited by the common
  // eligibility rule in applyEntityCrossFieldRules.
  complex: { required: ['complexDetails'], allowed: ['complexDetails'] },
};

function applyEntityCrossFieldRules(doc: any, ctx: z.RefinementCtx): void {
  if (doc.complexAnchor !== undefined && doc.complexMembership !== undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['complexMembership'],
      message: 'complexAnchor and complexMembership are mutually exclusive',
    });
  }

  // Inspection-complex facets are restricted to eligible categories
  // (building | parcel). Sites are the full location hierarchy and can never
  // carry a complex facet; residential/unit/hydrant/complex are likewise
  // rejected on any new validated write.
  if (!isComplexEligibleEntityCategory(doc.entityCategory)) {
    if (doc.complexAnchor !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['complexAnchor'],
        message: `complexAnchor is not valid for entityCategory '${doc.entityCategory}' — only building|parcel may anchor an inspection complex`,
      });
    }
    if (doc.complexMembership !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['complexMembership'],
        message: `complexMembership is not valid for entityCategory '${doc.entityCategory}' — only building|parcel may join an inspection complex`,
      });
    }
  }
  // Homogeneous membership: the stored anchor-category snapshot must equal the
  // member's own category so offline/full-document validation can prove it.
  if (
    doc.complexMembership !== undefined &&
    doc.entityCategory !== doc.complexMembership.anchorEntityCategory
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['complexMembership', 'anchorEntityCategory'],
      message: 'complexMembership.anchorEntityCategory must equal the entity category — complex membership is homogeneous',
    });
  }

  const rule = CATEGORY_RULES[doc.entityCategory as keyof typeof CATEGORY_RULES];
  if (!rule) return;

  for (const key of CATEGORY_DETAIL_KEYS) {
    if (doc[key] !== undefined && !rule.allowed.includes(key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [key],
        message: `${key} is not valid for entityCategory '${doc.entityCategory}'`,
      });
    }
  }
  for (const key of rule.required) {
    if (doc[key] === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [key],
        message: `${key} is required for entityCategory '${doc.entityCategory}'`,
      });
    }
  }
  // Category-owned non-detail fields (document 11 §7): crrProfile belongs to
  // residential; hydrantDefects/hydrantFlowTests belong to hydrant.
  if (doc.entityCategory !== 'residential' && doc.crrProfile !== undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['crrProfile'],
      message: `crrProfile is not valid for entityCategory '${doc.entityCategory}'`,
    });
  }
  if (doc.entityCategory !== 'hydrant') {
    for (const key of ['hydrantDefects', 'hydrantFlowTests'] as const) {
      if (doc[key] !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key],
          message: `${key} is not valid for entityCategory '${doc.entityCategory}'`,
        });
      }
    }
  }
  rule.extra?.(doc, ctx);
}

/**
 * @contract inspectableEntityDocumentSchema
 * Purpose: Complete target `inspectableEntities` document (schemaVersion 1).
 *   Strict at the containing boundary; enforces facet mutual exclusion and
 *   per-category detail discrimination so stale `never` fields are rejected
 *   on new writes.
 * Sync: Consumed by REST create/update merge validation and the sync-push
 *   boundary helper.
 */
export const inspectableEntityDocumentSchema = inspectableEntityWritableSchema
  .extend({
    _id: objectIdLikeSchema,
    schemaVersion: z.literal(1),
    // Derived compatibility projection — non-authoritative. The route derives
    // it from entityLocation.address; persisted copies on existing grid rows
    // remain readable, but no client may write it (absent from the writable
    // schema above, per document 11 §5).
    entityAddress: z.string().optional(),
    // Legacy/archive read-compatibility only — not client-writable target
    // fields (archive lifecycle is a dedicated route/server concern).
    isArchived: z.boolean().optional(),
    archivedAt: isoDateLikeSchema.optional(),
    archivedBy: objectIdLikeSchema.optional(),
    // NOTE: document 11 places `legacySource` on groups and membership facets,
    // not the complete entity base — entity provenance is `sourceMetadata`.
  })
  .merge(auditFieldsSchema)
  .merge(tombstoneFieldsSchema)
  .strict()
  .superRefine(applyEntityCrossFieldRules);

/**
 * @process isCanonicalEntityDocument
 * Steps: require the schemaVersion:1 target-row marker or either complex
 *   facet to be present.
 * Inputs: document — any entity-like value (HTTP body, Mongo row, Dexie row).
 * Outputs: true only for canonical (schemaVersion:1 / complex-faceted) entity
 *   documents, whose contact links are owned by the canonical sync path.
 * Failure Handling: never throws — non-object values return false.
 * Idempotency: REQUIRED — pure predicate.
 */
export function isCanonicalEntityDocument(document: unknown): boolean {
  if (!document || typeof document !== 'object' || Array.isArray(document)) return false;
  const doc = document as Record<string, unknown>;
  return doc.schemaVersion === 1 ||
    doc.complexAnchor !== undefined ||
    doc.complexMembership !== undefined;
}

/** Mongo match fragment selecting canonical entity documents (see `isCanonicalEntityDocument`). */
export const canonicalEntityDocumentMatch: { $or: Array<Record<string, unknown>> } = {
  $or: [
    { schemaVersion: 1 },
    { complexAnchor: { $exists: true } },
    { complexMembership: { $exists: true } },
  ],
};

/** Mongo match fragment selecting legacy (pre-contract) entity documents — the exact negation. */
export const legacyEntityDocumentMatch: { $nor: Array<Record<string, unknown>> } = {
  $nor: canonicalEntityDocumentMatch.$or.map((clause) => ({ ...clause })),
};

/**
 * @process normalizeInspectableEntityForSync
 * Steps: preserve legacy non-target documents; for schemaVersion:1/complex
 *   targets, omit null address subfields, repair malformed optional contact
 *   channels, and normalize legacy contact links.
 * Inputs: pulled or written entity document.
 * Outputs: shallow normalized document, preserving all unrelated fields.
 * Failure Handling: Missing/invalid contact ids are treated as orphan links;
 *   malformed required origin fields still fail strict document validation.
 * Idempotency: REQUIRED — already canonical documents are returned unchanged.
 * @errorHandling
 * Strategy: Keep contact identity and valid role origins, discard invalid
 *   optional email/phone/legacy role projections, drop BSON-null optional
 *   snapshot scalars, then validate the full target.
 * User Impact: Optional contact channels do not block entity/complex writes;
 *   malformed core entity or contact-origin data still fails validation.
 * Logging: No logging; the boundary caller reports sanitized validation failures.
 * @security
 * Validation: Canonical contact links are checked against `entityContactLinkSchema`.
 * Trust Boundary: Pulled server data remains untrusted until the containing document schema succeeds.
 */
export function normalizeInspectableEntityForSync<T extends Record<string, any>>(document: T): T {
  if (!isCanonicalEntityDocument(document)) return document;

  const normalized: Record<string, any> = { ...document };
  // No entity field is nullable by contract: BSON nulls left by earlier
  // writers (`notes`, `description`, …) are absent values, so heal them here.
  for (const key of Object.keys(normalized)) {
    if (normalized[key] === null) delete normalized[key];
  }
  if (normalized.entityLocation?.address && typeof normalized.entityLocation.address === 'object') {
    normalized.entityLocation = {
      ...normalized.entityLocation,
      address: Object.fromEntries(
        Object.entries(normalized.entityLocation.address).filter(([, value]) => value !== null),
      ),
    };
  }
  if (Array.isArray(normalized.contacts)) {
    const updatedAt = normalized.updatedAt instanceof Date
      ? normalized.updatedAt
      : new Date(String(normalized.updatedAt ?? normalized.createdAt));
    normalized.contacts = normalized.contacts.map((contact: unknown) => {
      if (
        !contact ||
        typeof contact !== 'object' ||
        Array.isArray(contact) ||
        !objectIdLikeSchema.safeParse((contact as Record<string, unknown>).contactId).success
      ) {
        return null;
      }
      if (entityContactLinkSchema.safeParse(contact).success) return contact;
      const contactRecord = contact as Record<string, any>;
      const snapshot = normalizeContactSnapshot(
        contactRecord.snapshot ?? {
          emails: contactRecord.emails,
          phoneNumbers: contactRecord.phoneNumbers,
          affiliations: contactRecord.affiliation ? [contactRecord.affiliation] : undefined,
        },
        contactRecord.name,
        updatedAt,
      );

      if (Array.isArray(contactRecord.linkOrigins) && contactRecord.linkOrigins.length > 0) {
        const linkOrigins = contactRecord.linkOrigins.map((origin: Record<string, any>) => ({
          ...origin,
          roles: normalizeLookupReferences(origin?.roles),
        }));
        const effectiveRoles = deduplicateLookupReferences(
          linkOrigins.flatMap((origin: Record<string, any>) => origin.roles),
        );
        const primary = linkOrigins.some((origin: Record<string, any>) => origin.isPrimary === true);
        const repaired: Record<string, any> = {
          ...contactRecord,
          schemaVersion: 1,
          snapshot,
          linkOrigins,
          effectiveRoles,
          isPrimary: primary,
        };
        for (const key of Object.keys(repaired)) {
          if (repaired[key] === null) delete repaired[key];
        }
        if (!lookupReferenceSchema.safeParse(repaired.role).success) delete repaired.role;
        if (repaired.contactType !== 'responsible' && repaired.contactType !== 'contact') {
          delete repaired.contactType;
        }
        return entityContactLinkSchema.parse(repaired);
      }

      const legacyContact: Record<string, any> = {
        contactId: contactRecord.contactId,
        snapshot,
        emails: snapshot.emails,
        phoneNumbers: snapshot.phoneNumbers,
      };
      // Whitelist-build the legacy ref: imported rows carry free-form extra
      // keys and malformed values that the strict link schemas must never see.
      if (lookupReferenceSchema.safeParse(contactRecord.role).success) legacyContact.role = contactRecord.role;
      if (contactRecord.contactType === 'responsible' || contactRecord.contactType === 'contact') {
        legacyContact.contactType = contactRecord.contactType;
      }
      if (typeof contactRecord.name === 'string') legacyContact.name = contactRecord.name;
      if (typeof contactRecord.title === 'string') legacyContact.title = contactRecord.title;
      if (typeof contactRecord.notes === 'string') legacyContact.notes = contactRecord.notes;
      if (typeof contactRecord.isPrimary === 'boolean') legacyContact.isPrimary = contactRecord.isPrimary;
      if (isoDateLikeSchema.safeParse(contactRecord.updatedAt).success) legacyContact.updatedAt = contactRecord.updatedAt;
      if (isoDateLikeSchema.safeParse(contactRecord.linkedAt).success) legacyContact.linkedAt = contactRecord.linkedAt;
      const affiliation = contactRecord.affiliation;
      if (affiliation && typeof affiliation === 'object' && !Array.isArray(affiliation) &&
        typeof (affiliation as Record<string, unknown>).organizationName === 'string') {
        legacyContact.affiliation = affiliation;
      }
      return normalizeEntityContactLink(
        legacyContact,
        normalized.updatedBy ?? normalized.createdBy,
        updatedAt,
      );
    }).filter((contact: unknown) => contact !== null);
  }
  return normalized as T;
}

function normalizeContactSnapshot(
  value: unknown,
  fallbackDisplayName: unknown,
  fallbackUpdatedAt: unknown,
): Record<string, unknown> {
  const snapshot = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
  // Optional scalar keys persisted with malformed values (BSON null from
  // earlier writers, or non-string garbage from imported rows) fail the
  // strict snapshot contract; drop them so poisoned rows heal here.
  // (mailingAddress is nullable by contract and is left untouched.)
  const healed: Record<string, unknown> = { ...snapshot };
  for (const key of ['firstName', 'lastName', 'title', 'company', 'address']) {
    const value = healed[key];
    if (value !== undefined && typeof value !== 'string') delete healed[key];
  }
  if (healed.affiliations !== undefined && !Array.isArray(healed.affiliations)) {
    delete healed.affiliations;
  }
  return {
    ...healed,
    displayName: typeof snapshot.displayName === 'string'
      ? snapshot.displayName
      : typeof fallbackDisplayName === 'string'
        ? fallbackDisplayName
        : '',
    emails: normalizeOptionalChannels(snapshot.emails, contactEmailSchema),
    phoneNumbers: normalizeOptionalChannels(snapshot.phoneNumbers, contactPhoneSchema),
    canonicalUpdatedAt: snapshot.canonicalUpdatedAt ?? fallbackUpdatedAt,
  };
}

function normalizeOptionalChannels<T>(
  values: unknown,
  schema: { safeParse(value: unknown): { success: boolean; data?: T } },
): T[] {
  if (!Array.isArray(values)) return [];
  return values.flatMap((value) => {
    const parsed = schema.safeParse(value);
    return parsed.success && parsed.data !== undefined ? [parsed.data] : [];
  });
}

function normalizeLookupReferences(values: unknown): unknown[] {
  if (!Array.isArray(values)) return [];
  return values.filter((value) => lookupReferenceSchema.safeParse(value).success);
}

function deduplicateLookupReferences(values: unknown[]): unknown[] {
  const seen = new Set<string>();
  return values.filter((value) => {
    if (!value || typeof value !== 'object') return false;
    const role = value as Record<string, unknown>;
    const key = `${String(role.lookupId)}::${String(role.code)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// ---------------------------------------------------------------------------
// inspectableEntityGroups document (document 11 §3)
// ---------------------------------------------------------------------------

const complexAnchorHistoryEntrySchema = z
  .object({
    entityId: objectIdLikeSchema,
    entityCategory: entityCategorySchema,
    entityName: z.string(),
    entityAddress: z.string().optional(),
    startedAt: isoDateLikeSchema,
    startedBy: objectIdLikeSchema,
    endedAt: isoDateLikeSchema.optional(),
    endedBy: objectIdLikeSchema.optional(),
    reason: z.string(),
  })
  .strict();

/**
 * Shared group-document construction. The ONLY difference between variants is
 * the current `anchor.entityCategory` schema; every other field, refinement,
 * strict boundary, audit/tombstone merge, and contact-uniqueness rule is
 * identical.
 */
function buildEntityGroupDocumentSchema<T extends z.ZodTypeAny>(currentAnchorCategorySchema: T) {
  return z
    .object({
      _id: objectIdLikeSchema,
      schemaVersion: z.literal(1),
      groupKind: z.literal('complex'),
      name: z.string(),
      nameKey: z.string().optional(),
      description: z.string().optional(),
      complexType: lookupReferenceSchema.optional(),
      anchor: z
        .object({
          entityId: objectIdLikeSchema,
          entityCategory: currentAnchorCategorySchema,
          entityName: z.string(),
          entityAddress: z.string().optional(),
        })
        .strict(),
      status: z.enum(['active', 'inactive']),
      contacts: z.array(complexGroupContactLinkSchema),
      anchorHistory: z.array(complexAnchorHistoryEntrySchema),
      deactivatedAt: isoDateLikeSchema.optional(),
      deactivatedBy: objectIdLikeSchema.optional(),
      deactivationReason: z.string().optional(),
      legacySource: legacySourceSchema.optional(),
    })
    .merge(auditFieldsSchema)
    .merge(tombstoneFieldsSchema)
    .strict()
    .superRefine((doc, ctx) => {
      const seen = new Set<string>();
      doc.contacts.forEach((link, index) => {
        const id = String(link.contactId);
        if (seen.has(id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['contacts', index, 'contactId'],
            message: 'group contacts must contain exactly one link per contactId',
          });
        }
        seen.add(id);
      });
    });
}

/**
 * @contract inspectableEntityGroupDocumentSchema
 * Purpose: Stable complex identity document. Owns name/type, current anchor
 *   snapshot, complex-wide contact links, anchor history, lifecycle, and
 *   tombstone. Never embeds member entity/case/report documents — members are
 *   found via `inspectableEntities.complexMembership.groupId`.
 * Fields: nameKey is the server-derived normalized name used to enforce
 *   case-insensitive uniqueness; legacy group documents may omit it.
 * Rules: exactly one contact link per contactId; `groupKind` is `complex`;
 *   `anchor` is the authoritative current anchor snapshot. Current anchors
 *   must be complex-eligible (building | parcel); `anchorHistory` keeps the
 *   broad `entityCategorySchema` as immutable legacy evidence.
 */
export const inspectableEntityGroupDocumentSchema =
  buildEntityGroupDocumentSchema(complexEligibleEntityCategorySchema);

/**
 * @contract inspectableEntityGroupCleanupDocumentSchema
 * Purpose: Cleanup-only variant of the group document. Identical strict
 *   shape, refinements, audit/tombstone merges, and contact uniqueness as
 *   `inspectableEntityGroupDocumentSchema`, except the CURRENT
 *   `anchor.entityCategory` accepts the broad `entityCategorySchema` so
 *   detach/delete cleanup paths can tombstone or repair a pre-existing
 *   malformed group (e.g. a legacy Site anchor) that the strict target
 *   schema would refuse to rewrite.
 * Rules: ONLY permitted for cleanup/detach/delete operation seams. It is NOT
 *   accepted for create/add/restore/transfer/visit or normal sync-target
 *   writes — those always use `inspectableEntityGroupDocumentSchema`.
 *   Anchor history stays broad in both variants as immutable evidence.
 */
export const inspectableEntityGroupCleanupDocumentSchema =
  buildEntityGroupDocumentSchema(entityCategorySchema);

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------

export type InspectableEntityDocument = z.infer<typeof inspectableEntityDocumentSchema>;
export type InspectableEntityGroupDocument = z.infer<typeof inspectableEntityGroupDocumentSchema>;
export type InspectableEntityGroupCleanupDocument = z.infer<typeof inspectableEntityGroupCleanupDocumentSchema>;
export type InspectableEntityWritable = z.infer<typeof inspectableEntityWritableSchema>;
export type ComplexAnchorLink = z.infer<typeof complexAnchorLinkSchema>;
export type ComplexMembership = z.infer<typeof complexMembershipSchema>;
